import { useState, useEffect, useCallback } from 'react';
import { Plus, Mail, CheckCircle2, AlertCircle, RefreshCw, X, Building2, DatabaseZap, Copy, ExternalLink } from 'lucide-react';
import { api, VendorWithStatus, CreateVendorData, UpdateVendorData } from '../api';
import { PageHeader, SectionCard, StatusBadge, EmptyState, DataTableSkeleton, useToast, ToastContainer } from '../components/ui';

// ── Invite Result Modal ────────────────────────────────────────────────────

function InviteResultModal({
  result,
  onClose,
}: {
  result: { email: string; inviteUrl: string; emailSent: boolean; emailError?: string; expiresAt: string };
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(result.inviteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const expiryDate = new Date(result.expiresAt).toLocaleString();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md"
        style={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4 }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <div className="flex items-center gap-2">
            {result.emailSent
              ? <CheckCircle2 className="w-4 h-4" style={{ color: '#4ade80' }} />
              : <AlertCircle className="w-4 h-4" style={{ color: '#fbbf24' }} />
            }
            <h2 className="font-display font-bold text-white text-sm">
              {result.emailSent ? 'Invite Sent' : 'Invite Link Created'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white/80 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {result.emailSent ? (
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.55)' }}>
              Invite email sent to <span className="text-white font-medium">{result.email}</span>. The link expires on {expiryDate}.
            </p>
          ) : (
            <div className="space-y-2">
              <p className="text-sm" style={{ color: '#fbbf24' }}>
                Email could not be sent{result.emailError ? ` — ${result.emailError}` : ''}.
              </p>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
                Share the link below with <strong className="text-white/70">{result.email}</strong> manually. It expires on {expiryDate}.
              </p>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'rgba(255,255,255,0.35)' }}>
              {result.emailSent ? 'Backup Invite Link' : 'Invite Link'}
            </label>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={result.inviteUrl}
                className="input flex-1 text-xs font-mono"
                style={{ color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}
                onFocus={e => e.target.select()}
              />
              <button
                onClick={copy}
                className="btn-ghost px-3 py-2 shrink-0 flex items-center gap-1.5 text-xs"
                style={copied ? { color: '#4ade80' } : {}}>
                {copied ? <><CheckCircle2 className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
              </button>
              <a href={result.inviteUrl} target="_blank" rel="noopener noreferrer"
                className="btn-ghost px-2 py-2 shrink-0">
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>

        <div className="px-6 pb-5 flex justify-end">
          <button onClick={onClose} className="btn-primary text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}


// ── Status badge helpers ───────────────────────────────────────────────────

function AccountBadge({ vendor }: { vendor: VendorWithStatus }) {
  if (vendor.has_password) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5"
        style={{ backgroundColor: 'rgba(74,222,128,0.08)', color: '#4ade80', borderRadius: 2, border: '1px solid rgba(74,222,128,0.18)' }}>
        <CheckCircle2 className="w-3 h-3" /> Active
      </span>
    );
  }
  if (vendor.has_user) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5"
        style={{ backgroundColor: 'rgba(251,191,36,0.08)', color: '#fbbf24', borderRadius: 2, border: '1px solid rgba(251,191,36,0.18)' }}>
        <Mail className="w-3 h-3" /> Invited
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5"
      style={{ backgroundColor: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.35)', borderRadius: 2, border: '1px solid rgba(255,255,255,0.08)' }}>
      No Account
    </span>
  );
}

// ── Vendor Form Modal ──────────────────────────────────────────────────────

interface FormState {
  vendor_id: string;
  name: string;
  contact_email: string;
  contact_phone: string;
  status: string;
  ships_for_us: number;
  shipping_cost_type: string;
  production_lead_time_days: string;
  shipping_lead_time_days: string;
  notes: string;
}

const DEFAULT_FORM: FormState = {
  vendor_id: '', name: '', contact_email: '', contact_phone: '',
  status: 'active', ships_for_us: 1, shipping_cost_type: 'quote',
  production_lead_time_days: '', shipping_lead_time_days: '', notes: '',
};

function VendorModal({
  vendor,
  onClose,
  onSaved,
}: {
  vendor: VendorWithStatus | null;
  onClose: () => void;
  onSaved: (msg: string, migRequired?: boolean, inviteData?: { email: string; inviteUrl: string; emailSent: boolean; emailError?: string; expiresAt: string }) => void;
}) {
  const isEdit = !!vendor;
  const [form, setForm] = useState<FormState>(
    vendor ? {
      vendor_id: vendor.vendor_id,
      name: vendor.name,
      contact_email: vendor.contact_email ?? '',
      contact_phone: vendor.contact_phone ?? '',
      status: vendor.status,
      ships_for_us: vendor.ships_for_us,
      shipping_cost_type: vendor.shipping_cost_type,
      production_lead_time_days: String(vendor.production_lead_time_days ?? ''),
      shipping_lead_time_days: String(vendor.shipping_lead_time_days ?? ''),
      notes: vendor.notes ?? '',
    } : DEFAULT_FORM
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const set = (k: keyof FormState, v: string | number) =>
    setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEdit && !form.contact_email.trim()) {
      setError('Contact email is required to send the vendor an invite.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      if (isEdit) {
        const data: UpdateVendorData = {
          name: form.name,
          status: form.status as 'active' | 'inactive',
          contact_email: form.contact_email || undefined,
          contact_phone: form.contact_phone || undefined,
          ships_for_us: form.ships_for_us,
          shipping_cost_type: form.shipping_cost_type as 'flat' | 'per_item' | 'included' | 'quote',
          production_lead_time_days: form.production_lead_time_days ? parseInt(form.production_lead_time_days) : undefined,
          shipping_lead_time_days: form.shipping_lead_time_days ? parseInt(form.shipping_lead_time_days) : undefined,
          notes: form.notes || undefined,
        };
        await api.updateVendor(vendor!.id, data);
        onSaved('Vendor updated.');
      } else {
        const data: CreateVendorData = {
          vendor_id: form.vendor_id.trim(),
          name: form.name.trim(),
          contact_email: form.contact_email.trim(),
          contact_phone: form.contact_phone || undefined,
          status: form.status as 'active' | 'inactive',
          ships_for_us: form.ships_for_us,
          shipping_cost_type: form.shipping_cost_type as 'flat' | 'per_item' | 'included' | 'quote',
          production_lead_time_days: form.production_lead_time_days ? parseInt(form.production_lead_time_days) : undefined,
          shipping_lead_time_days: form.shipping_lead_time_days ? parseInt(form.shipping_lead_time_days) : undefined,
          notes: form.notes || undefined,
        };
        const res = await api.createVendor(data);
        const migRequired = !!res.invite_error?.includes('DATABASE_MIGRATION_REQUIRED');
        const inviteData = res.invite_url ? {
          email: data.contact_email,
          inviteUrl: res.invite_url,
          emailSent: res.invite_sent,
          emailError: res.invite_error && !migRequired ? res.invite_error : undefined,
          expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
        } : undefined;
        onSaved('Vendor created.', migRequired, inviteData);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4 }}>
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
          <h2 className="font-display font-bold text-white">{isEdit ? `Edit — ${vendor!.name}` : 'Add Vendor'}</h2>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white/80 transition-colors"><X className="w-4 h-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-start gap-2 p-3 text-sm text-red-400"
              style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.18)', borderRadius: 2 }}>
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Vendor ID *</label>
              <input type="text" className="input w-full"
                value={form.vendor_id} onChange={e => set('vendor_id', e.target.value)}
                placeholder="eastbridge-supply" required disabled={isEdit} />
              <p className="text-[11px] text-white/25 mt-1">Unique slug — cannot change later</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Company Name *</label>
              <input type="text" className="input w-full"
                value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Eastbridge Supply Co." required />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">
                Contact Email {!isEdit && <span style={{ color: '#FFD400' }}>*</span>}
              </label>
              <input type="email" className="input w-full"
                value={form.contact_email} onChange={e => set('contact_email', e.target.value)}
                placeholder="vendor@company.com" required={!isEdit} />
              {!isEdit && <p className="text-[11px] text-white/25 mt-1">Invite will be sent to this address</p>}
            </div>
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Contact Phone</label>
              <input type="tel" className="input w-full"
                value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)}
                placeholder="+1 (555) 000-0000" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Status</label>
              <select className="input w-full" value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Ships For Us</label>
              <select className="input w-full" value={form.ships_for_us} onChange={e => set('ships_for_us', parseInt(e.target.value))}>
                <option value={1}>Yes</option>
                <option value={0}>No</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Shipping Cost</label>
              <select className="input w-full" value={form.shipping_cost_type} onChange={e => set('shipping_cost_type', e.target.value)}>
                <option value="quote">Quote</option>
                <option value="flat">Flat Rate</option>
                <option value="per_item">Per Item</option>
                <option value="included">Included</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Prod. Lead (days)</label>
              <input type="number" className="input w-full" min="0"
                value={form.production_lead_time_days} onChange={e => set('production_lead_time_days', e.target.value)}
                placeholder="7" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Ship Lead (days)</label>
              <input type="number" className="input w-full" min="0"
                value={form.shipping_lead_time_days} onChange={e => set('shipping_lead_time_days', e.target.value)}
                placeholder="3" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-white/50 uppercase tracking-wider mb-1.5">Admin Notes</label>
            <textarea className="input w-full" rows={2}
              value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Internal notes about this vendor..." />
          </div>

          {!isEdit && (
            <div className="flex items-start gap-2 p-3 text-xs text-white/50"
              style={{ backgroundColor: 'rgba(255,212,0,0.05)', border: '1px solid rgba(255,212,0,0.12)', borderRadius: 2 }}>
              <Mail className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: '#FFD400' }} />
              An invite email will be sent to the contact email automatically. The vendor will have 48 hours to set their password.
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
            <button type="submit" disabled={loading} className="btn-primary">
              {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create & Send Invite')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

export default function AdminVendors() {
  const { toasts, addToast, removeToast } = useToast();
  const [vendors, setVendors] = useState<VendorWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ open: boolean; vendor: VendorWithStatus | null }>({ open: false, vendor: null });
  const [sendingInvite, setSendingInvite] = useState<number | null>(null);
  const [migrationWarning, setMigrationWarning] = useState(false);
  const [inviteResult, setInviteResult] = useState<{ email: string; inviteUrl: string; emailSent: boolean; emailError?: string; expiresAt: string } | null>(null);

  const loadVendors = useCallback(async () => {
    try {
      const { vendors } = await api.getVendors();
      setVendors(vendors);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to load vendors', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadVendors(); }, [loadVendors]);

  const handleSaved = (
    msg: string,
    migRequired?: boolean,
    inviteData?: { email: string; inviteUrl: string; emailSent: boolean; emailError?: string; expiresAt: string }
  ) => {
    setModal({ open: false, vendor: null });
    if (migRequired) {
      setMigrationWarning(true);
      addToast('Vendor created. Migration required to send invite — see banner.', 'warning');
    } else if (inviteData) {
      setInviteResult(inviteData);
    } else {
      addToast(msg, 'success');
    }
    loadVendors();
  };

  const handleResendInvite = async (vendor: VendorWithStatus) => {
    setSendingInvite(vendor.id);
    try {
      const result = await api.sendVendorInvite(vendor.id);
      // Always show the invite result modal so the admin has the URL
      setInviteResult({
        email: result.email,
        inviteUrl: result.inviteUrl,
        emailSent: result.email_sent,
        emailError: result.email_error,
        expiresAt: result.expiresAt,
      });
      loadVendors();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to send invite';
      if (msg.includes('DATABASE_MIGRATION_REQUIRED') || msg.includes('migration')) {
        setMigrationWarning(true);
        addToast('Migration required — see banner above.', 'error');
      } else {
        addToast(msg, 'error');
      }
    } finally {
      setSendingInvite(null);
    }
  };

  const handleToggleStatus = async (vendor: VendorWithStatus) => {
    const next = vendor.status === 'active' ? 'inactive' : 'active';
    try {
      await api.updateVendor(vendor.id, { status: next });
      addToast(`Vendor ${next === 'active' ? 'activated' : 'deactivated'}.`, 'info');
      loadVendors();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to update status', 'error');
    }
  };

  return (
    <>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader
          title="Vendors"
          subtitle="Manage vendor partners and their portal access"
          actions={
            <button className="btn-primary flex items-center gap-2"
              onClick={() => setModal({ open: true, vendor: null })}>
              <Plus className="w-4 h-4" /> Add Vendor
            </button>
          }
        />

        {migrationWarning && (
          <div className="flex items-start gap-3 p-4 rounded"
            style={{ backgroundColor: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.22)' }}>
            <DatabaseZap className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#fbbf24' }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold" style={{ color: '#fbbf24' }}>Database migration required</p>
              <p className="text-sm mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                The vendor portal migration hasn't been applied yet. Vendors were created successfully but invite emails cannot be sent until you run the migration.
              </p>
              <p className="text-xs mt-2 font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>
                Open your Supabase project → SQL Editor → paste and run: <strong className="text-white/50">portal/supabase/apply_vendor_migrations.sql</strong>
              </p>
            </div>
            <button onClick={() => setMigrationWarning(false)} className="shrink-0 text-white/30 hover:text-white/60 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <SectionCard>
          {loading ? (
            <DataTableSkeleton rows={4} cols={5} />
          ) : vendors.length === 0 ? (
            <EmptyState
              icon={<Building2 className="w-8 h-8 text-white/20" />}
              message="No vendors yet. Click 'Add Vendor' to create your first one."
            />
          ) : (
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Vendor</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>ID / Slug</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Account</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Status</th>
                  <th className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.35)' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map(vendor => (
                  <tr key={vendor.id} className="table-row">
                    <td className="px-5 py-3.5">
                      <p className="text-sm font-semibold text-white">{vendor.name}</p>
                      {vendor.contact_email && (
                        <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.35)' }}>{vendor.contact_email}</p>
                      )}
                      {vendor.contact_phone && (
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>{vendor.contact_phone}</p>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <code className="text-xs px-1.5 py-0.5"
                        style={{ backgroundColor: 'rgba(255,212,0,0.07)', color: '#FFD400', borderRadius: 2 }}>
                        {vendor.vendor_id}
                      </code>
                      {vendor.vendor_slug && (
                        <p className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>/{vendor.vendor_slug}</p>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <AccountBadge vendor={vendor} />
                      {vendor.user_email && vendor.user_email !== vendor.contact_email && (
                        <p className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.25)' }}>{vendor.user_email}</p>
                      )}
                    </td>
                    <td className="px-5 py-3.5">
                      <StatusBadge variant={vendor.status === 'active' ? 'success' : vendor.status === 'pending' ? 'warning' : 'neutral'}>
                        {vendor.status}
                      </StatusBadge>
                    </td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setModal({ open: true, vendor })}
                          className="btn-ghost text-xs py-1 px-2">
                          Edit
                        </button>
                        {/* Show "Send Invite" if no active account, "Resend" if invited but no password */}
                        {(!vendor.has_user || !vendor.has_password) && vendor.contact_email && (
                          <button
                            onClick={() => handleResendInvite(vendor)}
                            disabled={sendingInvite === vendor.id}
                            className="btn-ghost text-xs py-1 px-2 flex items-center gap-1"
                            style={{ color: '#FFD400' }}>
                            {sendingInvite === vendor.id
                              ? <><RefreshCw className="w-3 h-3 animate-spin" /> Sending...</>
                              : <><Mail className="w-3 h-3" /> {vendor.has_user ? 'Resend Invite' : 'Send Invite'}</>
                            }
                          </button>
                        )}
                        <button
                          onClick={() => handleToggleStatus(vendor)}
                          className="btn-ghost text-xs py-1 px-2"
                          style={{ color: 'rgba(255,255,255,0.35)' }}>
                          {vendor.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </SectionCard>
      </div>

      {modal.open && (
        <VendorModal
          vendor={modal.vendor}
          onClose={() => setModal({ open: false, vendor: null })}
          onSaved={handleSaved}
        />
      )}

      {inviteResult && (
        <InviteResultModal
          result={inviteResult}
          onClose={() => setInviteResult(null)}
        />
      )}

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
}
